/**
 * The four social posts for one Flight Line piece.
 *
 * Each platform is approved and published in its own right, so each card
 * carries its own buttons. A disabled Publish always states its reason:
 * "the piece isn't published yet", "over the limit by 140" and "no
 * LinkedIn page configured" are three different problems and a dimmed
 * button says none of them.
 *
 * There is no `locked` prop: the post is a separate object from the
 * article it promotes, so its copy stays editable even once the piece
 * itself is published. The only per-card lock is `status === 'published'`
 * — a post that already went out is not rewritten out from under it.
 */
import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Panel } from '../components/Panel'
import { Button } from '../components/Button'
import { api } from '../api/client'
import type { SectionDraftDetail, SocialOutcome, SocialPost } from '../api/client'
import { describeError } from '../lib/describeError'
import { POSTING_OFF } from '../editorial/social'

const STATUS_CLASS: Record<string, string> = {
  draft: 'bg-emerald-900/40 text-emerald-200',
  approved: 'bg-sky-900/40 text-sky-200',
  published: 'bg-ink-700 text-paper',
  rejected: 'bg-ink-700 text-ink-300',
  failed: 'bg-red-900/40 text-red-200',
}

export function SocialBlock({ draftId, social, orphaned, postingOn, onChange, beforeWrite }: {
  draftId: number
  social: SocialPost[]
  orphaned: string[]
  /** DraftDetail.social_posting_on. While false there is no Publish: the server refuses it (409). */
  postingOn: boolean
  onChange: () => void
  /** Run before each write; false refuses it (the editorial piece page saves its edits first). */
  beforeWrite?: () => Promise<boolean>
}) {
  const ready = async () => {
    if (beforeWrite && !(await beforeWrite())) throw new Error('Save your changes first')
  }
  const [edit, setEdit] = useState<Record<string, string>>({})
  const [outcomes, setOutcomes] = useState<SocialOutcome[] | null>(null)
  // Same reason DraftView resets `edit` on `id` (SectionDraftsPage.tsx):
  // without this, switching drafts leaks an unsaved edit or a stale
  // outcomes list from one draft's cards onto another's.
  useEffect(() => { setEdit({}); setOutcomes(null) }, [draftId])

  const act = useMutation({
    mutationFn: async ({ platform, verb }: { platform: string; verb: string }) =>
      (await ready(), api.post<SectionDraftDetail>(`/api/lab/section-drafts/${draftId}/social/${platform}/${verb}`, {})),
    // Rewrite's whole purpose is to replace what's on screen — leaving a
    // stale edit in place would hide the fresh copy, and a later Save
    // would clobber the rewrite with the old text.
    onSuccess: (_d, v) => { setEdit((e) => { const { [v.platform]: _drop, ...rest } = e; return rest }); onChange() },
  })
  const save = useMutation({
    mutationFn: async ({ platform, body }: { platform: string; body: string }) =>
      (await ready(), api.patch<SectionDraftDetail>(`/api/lab/section-drafts/${draftId}/social/${platform}`, { body })),
    onSuccess: (_d, v) => { setEdit((e) => { const { [v.platform]: _drop, ...rest } = e; return rest }); onChange() },
  })
  const generate = useMutation({
    mutationFn: async () => (await ready(), api.post<SectionDraftDetail>(`/api/lab/section-drafts/${draftId}/social/generate`, {})),
    onSuccess: () => onChange(),
  })
  const publishAll = useMutation({
    mutationFn: async () => (await ready(), api.post<{ outcomes: SocialOutcome[] }>(`/api/lab/section-drafts/${draftId}/social/publish-all`, {})),
    onSuccess: (d) => { setOutcomes(d.outcomes); onChange() },
  })

  if (social.length === 0) {
    return (
      <Panel label="Social">
        <p className="label mb-2">This piece has no posts. The agent writes them with the draft; this one missed out.</p>
        <Button onClick={() => generate.mutate()} disabled={generate.isPending}>
          {generate.isPending ? 'Writing…' : 'Generate posts'}
        </Button>
      </Panel>
    )
  }

  const anyApproved = social.some((s) => s.status === 'approved' && s.publishable)

  return (
    <Panel label="Social">
      {orphaned.length > 0 && (
        <p className="text-amber-200 text-[13px] mb-3">
          {orphaned.length} live post{orphaned.length === 1 ? '' : 's'} point at a piece that is not published:
          {' '}{orphaned.join(', ')}. Nothing here can pull them — take them down on the platform.
        </p>
      )}
      {outcomes && (
        <ul className="label mb-3 space-y-0.5">
          {outcomes.map((o) => (
            <li key={o.platform}>{o.platform}: {o.state}{o.detail ? ` — ${o.detail}` : ''}</li>
          ))}
        </ul>
      )}
      <div className="space-y-4">
        {social.map((s) => {
          const body = edit[s.platform] ?? s.body
          const dirty = edit[s.platform] !== undefined && edit[s.platform] !== s.body
          const done = s.status === 'published'
          return (
            <div key={s.platform} className="border border-ink-600 p-3">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="text-[12px] uppercase tracking-wider">{s.label}</span>
                <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${STATUS_CLASS[s.status] ?? ''}`}>{s.status}</span>
                <span className={`label ${s.over_by > 0 ? 'text-red-300' : ''}`}>{s.length} / {s.limit}</span>
                {s.permalink && <a className="label underline" href={s.permalink} target="_blank" rel="noreferrer">view the post</a>}
              </div>
              {s.ambiguous && (
                <p className="text-amber-200 text-[13px] mb-2">
                  {s.status === 'published'
                    ? 'This went out, but an earlier attempt on it may also have gone out — check the page for a duplicate.'
                    : 'This timed out after it was sent, so it may already be live. Check the page before retrying.'}
                </p>
              )}
              {s.last_error && !s.ambiguous && <p className="text-red-300 text-[13px] mb-2">{s.last_error}</p>}
              {s.image_url && <img src={s.image_url} alt="" className="max-h-40 mb-2 rounded border border-ink-600" />}
              <textarea rows={4} disabled={done} value={body}
                        onChange={(e) => setEdit({ ...edit, [s.platform]: e.target.value })}
                        className="w-full bg-ink-900 border border-ink-600 text-paper text-[14px] p-2 font-sans" />
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {dirty && (
                  <Button size="sm" onClick={() => save.mutate({ platform: s.platform, body })} disabled={save.isPending}>Save</Button>
                )}
                {!done && <Button size="sm" variant="outline" onClick={() => act.mutate({ platform: s.platform, verb: 'rewrite' })} disabled={act.isPending}>Rewrite</Button>}
                {!done && s.status !== 'approved' && <Button size="sm" onClick={() => act.mutate({ platform: s.platform, verb: 'approve' })} disabled={act.isPending || s.over_by > 0}>Approve</Button>}
                {!done && s.status !== 'rejected' && <Button size="sm" variant="outline" onClick={() => act.mutate({ platform: s.platform, verb: 'reject' })} disabled={act.isPending}>Reject</Button>}
                {!done && postingOn && (
                  <Button size="sm" onClick={() => act.mutate({ platform: s.platform, verb: 'publish' })} disabled={!s.publishable || act.isPending}>
                    {s.ambiguous ? 'Check the page, then retry' : 'Publish'}
                  </Button>
                )}
                {!done && postingOn && !s.publishable && s.blocked_reason && <span className="label">{s.blocked_reason}</span>}
              </div>
            </div>
          )
        })}
      </div>
      {act.isError && <p className="text-red-300 text-[13px] mt-2">{describeError(act.error)}</p>}
      {postingOn ? (
        <div className="mt-4 flex items-center gap-2">
          <Button onClick={() => publishAll.mutate()} disabled={!anyApproved || publishAll.isPending}>
            {publishAll.isPending ? 'Publishing…' : 'Publish all approved'}
          </Button>
          {!anyApproved && <span className="label">nothing is approved yet</span>}
        </div>
      ) : (
        <p className="label mt-4">{POSTING_OFF}</p>
      )}
    </Panel>
  )
}
